import {gql} from '@apollo/client';

import {ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  CachedDataWhenRefetchingNamedOption,
  QueryResultType,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ChatEventNode} from 'llm/chat-library/messages/MessageTypes';

import {ChatSessionQuery, ChatSessionQueryVariables} from './__generated/ChatSessionQuery';

export type ChatSessionNode = ExcludeInaccessible<ChatSessionQuery['chatSession']>;

export const CHAT_SESSION_QUERY = gql`
  query ChatSessionQuery($chatSessionId: StrictString!, $disableAllThreads: StrictBoolean = true)
  @engComponent(name: LLM)
  @skipAudit {
    chatSession(id: $chatSessionId) {
      ... on ChatSessionNode {
        id
        type
        modifiedAt
        # For getProgressToolCalls
        allThreads(disable: $disableAllThreads) {
          id
          events {
            ... on LLMMessageChatEventNode {
              id
              createdAt
              role
              messageBlocks {
                ... on LLMJSONMessageBlockNode {
                  content
                }
              }
            }
          }
        }
        threads {
          id
          status
          events {
            ... on DisplayMessageChatEventNode {
              id
              createdAt
              role
              agentType
              messageBlocks {
                ... on DisplayTextBlockNode {
                  text
                }
                ... on DisplayFileBlockNode {
                  externalFileId
                  filename
                  mediaType
                }
              }
            }
            ... on StateUpdateChatEventNode {
              id
              createdAt
              newState
            }
            ... on RequestStateUpdateChatEventNode {
              id
              createdAt
              publicDataEnabled
            }
          }
        }
      }
    }
  }
`;

export interface UseChatSessionEventsResult {
  // The loaded chat events. Note that even in an error state, this may be returned with the
  // last-known values.
  events: Array<ChatEventNode>;
  isLoading: boolean;
  errorMessage: string | null;
  threadStatus: string | null;
  refetch: () => void;
  chatType?: ChatType;
  // The raw query data, available when includeToolCalls is true.
  // Useful for extracting progress data from allThreads.
  chatSessionQueryData?: ChatSessionQuery;
  /**
   * The full chat session data. Available when data has loaded successfully.
   * Useful for computing progress data from allThreads when includeToolCalls is true.
   */
  chatSession?: ChatSessionNode;
}

const POLL_INTERVAL_MS = 1000 * 30;

interface TestOverrides {
  pollIntervalMs: number;
  // No-op in production, this can be overridden in tests to capture the refetch function to invoke directly.
  registerRefetch: (refetch: () => void) => void;
}

// Values overridable by test code, should not be imported except by tests.
export const _testOverrides: TestOverrides = {
  pollIntervalMs: POLL_INTERVAL_MS,
  registerRefetch: () => {},
};

/**
 * Hook to get the most recent chat messages/events for display purposes.
 *
 * Right now, this polls for events every second from the GraphQL endpoint, but the
 * intention is to use websockets in the future.
 */
export function useChatSessionEvents({
  chatSessionId,
  onEventsUpdated,
  includeToolCalls,
}: {
  chatSessionId: string | null;
  onEventsUpdated?: (events: Array<ChatEventNode>) => void;
  includeToolCalls: boolean;
}): UseChatSessionEventsResult {
  const queryResult = useSkippableBenchlingQuery<ChatSessionQuery, ChatSessionQueryVariables>(
    CHAT_SESSION_QUERY,
    {
      skip: !chatSessionId,
      variables: chatSessionId ? {chatSessionId, disableAllThreads: !includeToolCalls} : undefined,
      // LLMFeedbackChatToolbar state is reset on refetches without WHEN_LOADING.
      renderCachedDataWhenRefetching: CachedDataWhenRefetchingNamedOption.WHEN_LOADING,
      pollInterval: _testOverrides.pollIntervalMs,
      // Always fetch from network when component mounts to avoid stale data
      fetchPolicy: 'cache-and-network',
      onCompleted: (data) => {
        onEventsUpdated?.(
          data.chatSession.__typename === 'ChatSessionNode' ? data.chatSession.threads[0].events : []
        );
      },
    }
  );

  _testOverrides.registerRefetch(queryResult.refetch);

  if (queryResult.type === QueryResultType.ERROR) {
    // Even in an error state, we want to return any results we have. That way, we don't throw the
    // whole page into an error state on things like temporary network disconnects.
    const chatSession = queryResult.previousData?.chatSession;
    const chatSessionNode = chatSession != null && !isInaccessible(chatSession) ? chatSession : undefined;
    return {
      events: chatSession == null || isInaccessible(chatSession) ? [] : chatSession.threads[0].events,
      isLoading: false,
      errorMessage: 'An error occurred loading this chat. Please try again later.',
      threadStatus: chatSession == null || isInaccessible(chatSession) ? null : chatSession.threads[0].status,
      refetch: queryResult.refetch,
      chatSessionQueryData: includeToolCalls ? queryResult.previousData : undefined,
      chatSession: chatSessionNode,
    };
  } else if (queryResult.type === QueryResultType.SKIPPED) {
    return {
      events: [],
      isLoading: false,
      errorMessage: null,
      threadStatus: null,
      refetch: queryResult.refetch,
      chatSessionQueryData: undefined,
      chatSession: undefined,
    };
  } else if (queryResult.type === QueryResultType.PENDING) {
    return {
      events: [],
      isLoading: true,
      errorMessage: null,
      threadStatus: null,
      refetch: queryResult.refetch,
      chatSessionQueryData: undefined,
      chatSession: undefined,
    };
  } else {
    const {chatSession} = queryResult.data;
    if (isInaccessible(chatSession)) {
      return {
        events: [],
        isLoading: false,
        errorMessage: 'You do not have permission to access this chat.',
        threadStatus: null,
        refetch: queryResult.refetch,
        chatSessionQueryData: undefined,
        chatSession: undefined,
      };
    }
    const rootThread = chatSession.threads[0];
    return {
      events: rootThread.events,
      isLoading: false,
      errorMessage: null,
      refetch: queryResult.refetch,
      chatType: chatSession.type,
      threadStatus: rootThread.status,
      chatSessionQueryData: includeToolCalls ? queryResult.data : undefined,
      chatSession,
    };
  }
}
